import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Dialog from "@mui/material/Dialog";

import theme from "../../../styles/theme";
import { NoDataMessage } from "../../../components/Common/TableStyles";
import { LoadFailure } from "./LoadFailure";
import {
  CloseButton,
  Ellipsis,
  FilterInput,
  ModalHead,
  ModalHeadText,
  ModalTitle,
  PositionCell,
  TeamDot,
  isTeamKind,
  modalOverlaySx,
  modalPaperSx,
} from "../styles";

const Body = styled.div`
  padding: 12px 18px 18px;
  overflow: auto;
`;

const SearchRow = styled.div`
  padding: 14px 18px 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const HeadRow = styled.tr`
  height: 24px;

  th {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${theme.colors.grey};
    border-bottom: 2px solid ${theme.colors.secondaryDark};
    padding: 0 8px;
  }
`;

const Row = styled.tr`
  height: 40px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  &:hover {
    background: ${({ $clickable }) => ($clickable ? "#2a2a2a" : "transparent")};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.gold};
    outline-offset: -2px;
  }

  td {
    border-bottom: 2px solid ${theme.colors.secondaryDark};
    font-size: 13px;
    padding: 0 8px;
  }
`;

const HolderCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TeamCell = styled(Ellipsis)`
  font-size: 11px;
  color: ${theme.colors.grey};
  display: block;
  /* Matches the column width below — the modal is wide enough to give the team far more room
     than the tile does before it needs to ellipsise. */
  max-width: 260px;
`;

const Value = styled.span`
  font-size: 15px;
  font-weight: 700;
`;

// PositionCell defaults to Roboto Condensed; the tile this modal expands overrides it back to the
// page font, so the full table does the same rather than switching typeface on open.
const Position = styled(PositionCell)`
  font-family: inherit;
`;

export function BoardModal({ kind, simulatorId, onClose, loadFullBoard, onSelectDriver }) {
  const [board, setBoard] = useState(null);
  const [failed, setFailed] = useState(false);
  // Bumping this re-runs the load effect, which is the whole of "retry" — `cached()` only writes
  // on success, so a second attempt genuinely refetches rather than replaying the failure.
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState("");
  const isTeamBoard = isTeamKind(kind);

  useEffect(() => {
    let cancelled = false;
    setBoard(null);
    setFailed(false);
    setSearch("");
    if (kind) {
      loadFullBoard(kind, simulatorId)
        .then((result) => {
          if (!cancelled) setBoard(result);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [kind, simulatorId, loadFullBoard, attempt]);

  const modalTitle = failed ? "Повна таблиця" : "Завантаження…";
  const retry = () => setAttempt((prev) => prev + 1);

  const filteredRows = useMemo(() => {
    const rows = board?.rows || [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (row) => row.holderName?.toLowerCase().includes(q) || row.teamName?.toLowerCase().includes(q),
    );
  }, [board, search]);

  return (
    <Dialog
      open={Boolean(kind)}
      onClose={onClose}
      maxWidth={false}
      sx={{ ...modalOverlaySx, "& .MuiDialog-paper": { width: "100%", maxWidth: 760 } }}
      PaperProps={{ sx: modalPaperSx }}
    >
      <ModalHead>
        <ModalHeadText>
          <ModalTitle>{board ? `${board.title} · повна таблиця` : modalTitle}</ModalTitle>
        </ModalHeadText>
        <CloseButton onClick={onClose} aria-label="Закрити">
          ✕
        </CloseButton>
      </ModalHead>

      {!failed && (
        <SearchRow>
          <FilterInput
            style={{ width: "100%", boxSizing: "border-box" }}
            placeholder={isTeamBoard ? "Пошук команди…" : "Пошук пілота…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchRow>
      )}

      <Body>
        {failed ? (
          <LoadFailure onRetry={retry} />
        ) : filteredRows.length === 0 ? (
          <NoDataMessage>
            {search.trim()
              ? `За запитом «${search.trim()}» нічого не знайдено`
              : "Ще немає результатів"}
          </NoDataMessage>
        ) : (
          <Table>
            <thead>
              <HeadRow>
                <th style={{ width: 32 }}>#</th>
                <th>{isTeamBoard ? "Команда" : "Пілот"}</th>
                {!isTeamBoard && <th style={{ width: 260 }}>Команда</th>}
                <th style={{ width: 90, textAlign: "right" }}>{board?.valueLabel}</th>
              </HeadRow>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <Row
                  key={`${row.position}-${row.holderId}`}
                  $clickable={!isTeamBoard}
                  onClick={isTeamBoard ? undefined : () => onSelectDriver(row.holderId)}
                  tabIndex={isTeamBoard ? undefined : 0}
                  aria-label={isTeamBoard ? undefined : `${row.holderName} — профіль пілота`}
                  onKeyDown={
                    isTeamBoard
                      ? undefined
                      : (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onSelectDriver(row.holderId);
                          }
                        }
                  }
                >
                  <td>
                    <Position $position={row.position}>{row.position}</Position>
                  </td>
                  <td>
                    <HolderCell>
                      {row.teamColor && (
                        <TeamDot $color={row.teamColor} $size={isTeamBoard ? 10 : 8} />
                      )}
                      {row.holderName}
                    </HolderCell>
                  </td>
                  {!isTeamBoard && (
                    <td>
                      <TeamCell>{row.teamName}</TeamCell>
                    </td>
                  )}
                  <td style={{ textAlign: "right" }}>
                    <Value>{row.value}</Value>
                  </td>
                </Row>
              ))}
            </tbody>
          </Table>
        )}
      </Body>
    </Dialog>
  );
}
